import React from 'react';
import classNames from 'classnames';
import Button from '@mui/material/Button';
import { useRouter } from 'next/router';
import AddToQueueIcon from '@mui/icons-material/AddToQueue';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useAuth } from '@developers/single-sign-on';
import makeStyles from '@mui/styles/makeStyles';
import * as marketplaceAjax from '../../ajax/marketplace/vendors';
import { useAjax } from '@developers/use-ajax';

const useStyles = makeStyles({
  banner: {
    backgroundImage: 'url(/frp/img/stock_gym.jpg)',
    backgroundPosition: 'center center',
    backgroundColor: '#3f51b5',
    fontFamily: '\'Roboto\', \'Open Sans\', sans-serif',
    position: 'relative',
    marginBottom: 15,
    padding: '30px 0px 30px 0px',
  },
  bannerTitle: {
    color: '#FFF',
    fontSize: 36,
    textAlign: 'center',
  },
  bannerDescription: {
    color: '#FFF',
    fontSize: 16,
    margin: '15px auto',
    width: '70%',
  },
  button: {
    textAlign: 'center',
  },
  backdrop: {
    position: 'absolute',
    top: '0',
    bottom: '0',
    left: '0',
    right: '0',
    backgroundColor: 'rgba(48,57,66,0.6)',
  },
  section: {
    marginBottom: 100
  },
  overview: {
    textAlign: 'center',
  },
  overviewTitle: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  overviewText: {
    fontSize: 16,
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: 30,
    marginBottom: 10
  },
  sectionText: {
    fontSize: 16
  },
  icon: {
    fontSize: 60
  },
  callToActionRow: {
    textAlign: 'center',
    marginBottom: 100
  },
  callToActionDescription: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 15
  },
  callToActionButton: {
    fontSize: 30,
    marginBottom: 20
  },
  stockImage: {
    width: '100%',
    height: 'auto'
  },
  discountTable: {
    marginLeft: 30,
    marginTop: 15,
    maxWidth: 600
  }
});

function About() {
  const auth = useAuth();
  const classes = useStyles();
  let router = useRouter();
  const startSellingRef = React.useRef(null);
  const [_, registerAccount, registerAccountMeta] = useAjax(marketplaceAjax.register);

  const isRegistered = Boolean(auth.session_data.user?.account?.vendor?.id);
  
  function scrollToAccount() {
    startSellingRef.current.scrollIntoView({ behavior: 'smooth' });
  }
  
  function goToDashboard() {
    router.push('/marketplace/vendors/' + auth.session_data.user?.account?.vendor?.id + '/dashboard');
  }

  React.useEffect(() => {
    if (registerAccountMeta.success) {
      window.location.href = '/marketplace/vendors/x/dashboard';
    }
  }, [registerAccountMeta]);

  return (
    <div className="container-fluid">
      <div className="row" style={{ marginBottom: 30 }}>
        <div className={classNames('container-fluid', classes.banner)}>
          <div className={classes.backdrop}></div>
          <div className='row'>
            <div className='col-xs-12 col-sm-12'>
              <p className={classes.bannerTitle}>
                Sell your parts on FitnessRepairParts.com
              </p>
            </div>
          </div>
          <div className='row'>
            <div className='col-xs-12 col-sm-12'>
              <p className={classes.bannerDescription}>
                Reach a wide customer base by selling your fitness parts on the FitnessRepairParts.com Marketplace. List your products directly alongside our first-party catalog listings.
              </p>
            </div>
          </div>
          <div className='row'>
            <div className={classNames('col-xs-12', 'col-sm-12', classes.button)}>
              <Button color='primary' variant="contained" onClick={scrollToAccount}>
                Start Selling
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className={classNames('row', classes.section)}>
        <div className={classNames('container-fluid', classes.overview)}>
          <div className={classes.sectionTitle}>How it Works</div>
          <div className="col-sm-4">
            <AddToQueueIcon className={classes.icon} />
            <div className={classes.overviewTitle}>Add Inventory</div>
            <p className={classes.overviewText}>
              Import your inventory into our Marketplace dashboard. Approved products will appear <b>on the same store pages</b> as our first-party catalog listings.
            </p>
          </div>
          <div className="col-sm-4">
            <ShoppingCartIcon className={classes.icon} />
            <div className={classes.overviewTitle}>Sell Products</div>
            <p className={classes.overviewText}>
              Customers buy your products on our store pages. Funds are <b>immediately</b> and <b>directly</b> deposited into <b>your PayPal account</b>.
            </p>
          </div>
          <div className="col-sm-4">
            <LocalShippingIcon className={classes.icon} />
            <div className={classes.overviewTitle}>Ship Items</div>
            <p className={classes.overviewText}>
              You ship items directly to the customer. You both will have contact information to maintain customer relations.
            </p>
          </div>
        </div>
      </div>
      <div className={classNames('row', classes.section)}>
        <div className='container-fluid'>
          <div className="col-sm-6">
            <p className={classes.sectionTitle}>Reach a Wide Customer Base</p>
            <p className={classes.sectionText}>
              Every year thousands of customers buy parts from our catalog. Your products are listed on the same page as its matching catalog item. Once customers select that they want to buy your parts, they will be taken to the checkout process on <b>your PayPal account</b>. This provides a quick and smooth checkout experience for your customers.
            </p>
          </div>
          <div className="col-sm-6">
            <img className={classes.stockImage} src="/frp/img/marketplace_listing_preview.jpg" />
          </div>
        </div>
      </div>
      <div className={classNames('row', classes.section)}>
        <div className='container-fluid'>
          <div className="col-sm-6">
            <img className={classes.stockImage} src="/frp/img/stock_customers.jpg" />
          </div>
          <div className="col-sm-6">
            <p className={classes.sectionTitle}>Handle Customer Service Your Own Way</p>
            <p className={classes.sectionText}>
              We notify you when a customer places an order. You fulfill and ship the order to the customer directly. The customer receives your contact details when the order is placed, giving you complete control over their customer service experience. We provide easy-to-use tools to help customize things like shipping rates and return policies.
            </p>
          </div>
        </div>
      </div>
      <div className={classNames('row', classes.section)}>
        <div className='container-fluid'>
          <p className={classes.sectionTitle}>Pricing</p>
          <p className={classes.sectionText}>
            Customers make payments directly into your PayPal account. We charge a <b>15% commission rate</b> for all orders placed through the FitnessRepairParts Marketplace. At the end of the month we send you a bill for that month&lsquo;s orders.
          </p>
          <p className={classes.sectionText}>
            We provide a discount to our commission fee based on the dollar amount of that commission fee.
          </p>
          <table className={classNames('table table-striped', classes.discountTable)} >
            <thead className='thead-light'>
              <tr>
                <th>Commission Fee</th>
                <th>Tier Discount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>$500 - $999.99</td>
                <td>5%</td>
              </tr>
              <tr>
                <td>$1,000 - $2,999.99</td>
                <td>10%</td>
              </tr>
              <tr>
                <td>$3,000</td>
                <td>15%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="row">
        <div id="start-selling" ref={startSellingRef}>
          {isRegistered ? 
            <div className={classes.callToActionRow}>
              <div className={classes.callToActionButton} id="create-account">Manage Your Marketplace Account</div>
              <div className={classes.callToActionDescription}>
                You are already registered as a Marketplace vendor.
              </div>
              <Button color='primary' variant="contained" onClick={goToDashboard}>
                Go To Marketplace Dashboard
              </Button>
            </div>
          : 
            <RegisterButton 
              meta={registerAccountMeta}
              classes={classes}
              registerAccount={registerAccount}
              goToDashboard={goToDashboard}
              logged_in={auth.isAuthenticated}
            />
          }
        </div>
      </div>
    </div>
  );
}

export default About;

const RegisterButton = (props) => {
  if (!props.logged_in) {
    return (
      <div className={props.classes.callToActionRow}>
        <div className={props.classes.callToActionButton} id="create-account">Create Account</div>
        <div className={props.classes.callToActionDescription}>
          To get started as a Marketplace vendor, you first need to have a FitnessRepairParts account. Once your account has been activated, you can come back here to get registered as a vendor.
        </div>
        <Button 
          color='primary' 
          variant="contained" 
          href="/join.php"
          style={{color: 'white'}}
        >
          Create Account
        </Button>
      </div>
    );
  } else {
    return (
      <div className={props.classes.callToActionRow}>
        <div className={props.classes.callToActionButton} id="create-account">Register Account</div>
        <div className={props.classes.callToActionDescription}>
          Register your FitnessRepairParts account to become a Marketplace vendor. Once your request has been approved, you will receive email notification with further instructions on how to proceed.
        </div>
        <Button 
          color='primary' 
          variant="contained" 
          onClick={props.registerAccount}
          disabled={props.meta.loading}
        >
          Register Account
        </Button>
      </div>
    );
  }
}